import { useState, type ReactNode } from 'react';
import { api, ApiError, type EmbedCheck, type SiteDetail } from '../lib/api';
import { Alert, Button, Card, Spinner, cx } from '../components/ui';
import { PromptKarte } from './SitePromptCard';

/**
 * Einbauanleitung mit Fortschritt.
 *
 * Zwei Dinge bewusst kombiniert: Haken zum Abhaken für das, was nur der
 * Mensch wissen kann, und eine echte Prüfung der Website für alles, was
 * sich messen laesst. Eine Anleitung, die nur aus Kästchen besteht, ist so
 * verlässlich wie die Erinnerung dessen, der sie ausfuellt.
 */

interface Schritt {
  id: string;
  titel: string;
  beschreibung: ReactNode;
  code?: string;
  /** Automatischer Befund, sofern die Prüfung dazu etwas sagen kann. */
  befund?: { ok: boolean; text: string };
  /** true blendet den Schritt aus, wenn er für diese Website nicht zutrifft. */
  entfaellt?: boolean;
}

export function EmbedTab({
  site,
  snippet,
  onSaved,
}: {
  site: SiteDetail;
  snippet: string;
  onSaved: () => Promise<void>;
}) {
  const [pruefung, setPrüfung] = useState<EmbedCheck | null>(null);
  const [pruefeLaeuft, setPruefeLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [schritte, setSchritte] = useState<Record<string, string>>(site.setupSteps ?? {});

  async function pruefen() {
    setPruefeLaeuft(true);
    setFehler(null);
    try {
      setPrüfung(await api.checkEmbed(site.id));
    } catch (caught) {
      setFehler(caught instanceof ApiError ? caught.message : 'Prüfung fehlgeschlagen.');
    } finally {
      setPruefeLaeuft(false);
    }
  }

  async function umschalten(id: string) {
    const nächste = { ...schritte };
    if (nächste[id]) delete nächste[id];
    else nächste[id] = new Date().toISOString();
    setSchritte(nächste);
    try {
      await api.updateSite(site.id, { setupSteps: nächste });
      await onSaved();
    } catch {
      // Bei einem Fehler zurueckdrehen, sonst zeigt die Oberflaeche etwas an,
      // was nicht gespeichert wurde.
      setSchritte(schritte);
    }
  }

  const p = pruefung;
  const tracker = p?.ungeblockteTracker ?? [];
  const fremde = p?.fremdeCmp ?? [];
  const einbettungen = p?.einbettungen ?? [];

  const liste: Schritt[] = [
    {
      id: 'script',
      titel: 'Script-Tag als erstes Element in den <head>',
      beschreibung: (
        <>
          Muss <strong>vor</strong> allen anderen Skripten stehen, auch vor dem Tag Manager. Steht es weiter unten,
          feuern Tags, bevor blockiert werden kann.
        </>
      ),
      code: snippet,
      befund: p?.erreichbar
        ? p.einbindung?.vorhanden
          ? p.einbindung.zuerst
            ? {
                ok: (p.seiten ?? []).every((x) => x.status !== 200 || x.loader),
                text: `Auf der Startseite gefunden und vor allen anderen Skripten. ${
                  (p.seiten ?? []).filter((x) => x.loader).length
                } von ${(p.seiten ?? []).length} Seiten haben das Script.`,
              }
            : {
                ok: false,
                text: `Gefunden, aber es stehen ${p.einbindung.davorImKopf.length} Skripte davor: ${p.einbindung.davorImKopf
                  .map((s) => kurz(s))
                  .join(', ')}`,
              }
          : p.einbindung?.fremdeKennung.length
            ? {
                ok: false,
                text: `Ein Script von uns ist eingebunden, aber mit der Kennung einer anderen Website: ${p.einbindung.fremdeKennung
                  .map((s) => kurz(s))
                  .join(', ')}. Ersetze es durch das Snippet oben.`,
              }
            : { ok: false, text: 'Auf der Startseite nicht gefunden. Ist die Änderung hochgeladen?' }
        : undefined,
    },
    {
      id: 'cmp-entfernen',
      titel: 'Vorhandenes Consent-Tool entfernen',
      beschreibung:
        fremde.length > 0 ? (
          <>
            Zwei Consent-Tools auf einer Seite blockieren sich gegenseitig. Gefunden wurde{' '}
            <strong>{fremde.map((f) => f.name).join(', ')}</strong>. Diese Script-Tags löschen, auch das am Ende des
            Body und das für die Cookie-Erklärung.
            {fremde.some((f) => f.selbstBlockiert) && (
              <span className="mt-2 block rounded-lg bg-warn-100 px-3 py-2 text-xs text-[#7c2d12]">
                Nebenbefund: mindestens eines dieser Skripte trägt selbst <code>type="text/plain"</code> und wird vom
                Browser nie ausgeführt. Das Tool ist dort also gar nicht aktiv, obwohl es eingebunden aussieht.
              </span>
            )}
          </>
        ) : (
          <>Falls ein anderes Consent-Tool eingebunden ist, muss es raus. Zwei davon blockieren sich gegenseitig.</>
        ),
      code: fremde.length > 0 ? fremde.map((f) => `<!-- löschen: --> <script src="${f.src}"></script>`).join('\n') : undefined,
      befund: p?.erreichbar
        ? fremde.length === 0
          ? { ok: true, text: 'Kein fremdes Consent-Tool im HTML gefunden.' }
          : { ok: false, text: `${fremde.length} fremde Script-Tags gefunden.` }
        : undefined,
    },
    {
      id: 'tracker',
      titel: 'Tracking-Skripte blockierbar machen',
      beschreibung:
        tracker.length > 0 ? (
          <>
            Diese Skripte laufen aktuell ohne Einwilligung. Ersetze sie durch die Fassung unten:{' '}
            <code>type="text/plain"</code> verhindert die Ausführung, <code>data-ilc</code> sagt, ab welcher
            Einwilligung sie starten dürfen.
          </>
        ) : (
          <>
            Falls Tracking-Skripte fest im HTML stehen, markiere sie so. Das ist der sichere Weg, sicherer als das
            automatische Blockieren.
          </>
        ),
      code:
        tracker.length > 0
          ? tracker
              .map((t) => `<script type="text/plain" data-ilc="${t.empfohleneKategorie}" src="${t.src}"></script>`)
              .join('\n')
          : '<script type="text/plain" data-ilc="marketing" src="DEINE-SCRIPT-URL"></script>',
      befund: p?.erreichbar
        ? tracker.length === 0
          ? { ok: true, text: 'Keine unblockierten Tracking-Skripte im HTML gefunden.' }
          : { ok: false, text: `${tracker.length} laufen ohne Einwilligung: ${tracker.map((t) => t.name).join(', ')}` }
        : undefined,
    },
    {
      id: 'einbettungen',
      titel: 'Eingebettete Videos und Karten entschaerfen',
      beschreibung: (
        <>
          <strong>Das vorhandene iframe ersetzen, nicht ein zweites hinzufügen.</strong> Bei YouTube, Vimeo oder
          Google Maps reicht das automatische Blockieren nicht ganz: der Browser beginnt den Ladevorgang, bevor unser
          Script eingreifen kann. Cookies entstehen dadurch keine, aber die IP-Adresse des Besuchers geht schon vor der
          Einwilligung an den Anbieter. Sauber wird es nur, wenn im HTML gar kein <code>src</code> steht. Aus{' '}
          <code>src</code> wird <code>data-ilc-src</code>, dann lädt der Inhalt erst nach Zustimmung oder per Klick
          auf den Platzhalter.
          {einbettungen.length > 0 && (
            <span className="mt-2 block">
              Gefunden auf deiner Startseite: <strong>{einbettungen.map((e) => e.name).join(', ')}</strong>. Unten
              steht dein eigenes Markup, bereits umgeschrieben und mit allen vorhandenen Attributen.
            </span>
          )}
        </>
      ),
      code:
        einbettungen.length > 0
          ? einbettungen.map((e) => e.ersatz).join('\n\n')
          : '<iframe\n  data-ilc="marketing"\n  data-ilc-src="https://www.youtube.com/embed/VIDEO-ID"\n  title="Video"\n  allowfullscreen></iframe>',
      befund: p?.erreichbar
        ? einbettungen.length === 0
          ? { ok: true, text: 'Keine Einbettung mit direktem src gefunden.' }
          : {
              ok: false,
              text: `${einbettungen.length} ${einbettungen.length === 1 ? 'Einbettung lädt' : 'Einbettungen laden'} vor der Einwilligung: ${einbettungen
                .map((e) => e.name)
                .join(', ')}`,
            }
        : undefined,
    },
    {
      id: 'erklaerung',
      titel: 'Cookie-Erklärung auf der Datenschutzseite',
      beschreibung: <>Der Platzhalter wird automatisch mit der Liste aus dem letzten Scan gefüllt.</>,
      code: '<div data-ilc-declaration></div>',
      befund: p?.erreichbar
        ? p.hatErklaerung
          ? p.erklaerungSeiteHatLoader === false
            ? {
                ok: false,
                text: `Platzhalter gefunden auf ${p.erklaerungAufSeite}, aber dort fehlt das Script-Tag aus Schritt 1. Die Erklärung bleibt deshalb leer. Das Script gehört in den <head> jeder Seite.`,
              }
            : { ok: true, text: `Gefunden auf ${p.erklaerungAufSeite ?? 'der Startseite'}.` }
          : {
              ok: false,
              text: 'Weder auf der Startseite noch auf der verlinkten Datenschutzseite gefunden.',
            }
        : undefined,
    },
    {
      id: 'footer-link',
      titel: 'Link für den Widerruf in den Footer',
      beschreibung: (
        <>
          Pflicht: der Widerruf muss so einfach sein wie die Einwilligung. Der Link öffnet das Banner erneut. Er
          funktioniert erst, wenn Schritt 1 live ist, vorher passiert beim Klick nichts.
        </>
      ),
      code: '<a href="#" data-ilc-open>Cookie-Einstellungen</a>',
      befund: p?.erreichbar
        ? p.hatOpenLink
          ? { ok: true, text: 'Gefunden.' }
          : { ok: false, text: 'Auf der Startseite nicht gefunden.' }
        : undefined,
    },
    {
      id: 'scan',
      titel: 'Scannen und Kategorien prüfen',
      beschreibung: (
        <>
          Nach dem Einbau einen Scan starten (Reiter Übersicht). Danach im Reiter Cookies alles prüfen, was als
          ungeklärt markiert ist. Ungeklärte Einträge erscheinen nicht in der Erklärung und werden nicht blockiert.
        </>
      ),
    },
  ];

  const erledigt = liste.filter((s) => schritte[s.id]).length;

  return (
    <div className="flex flex-col gap-5">
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold tracking-tight">Einbau auf {site.domain}</h2>
            <p className="mt-1 text-sm text-[var(--text-muted)]">
              {erledigt} von {liste.length} Schritten erledigt
            </p>
          </div>
          <Button onClick={pruefen} loading={pruefeLaeuft} size="sm">
            Einbindung prüfen
          </Button>
        </div>

        <div className="mt-3 h-2 overflow-hidden rounded-full bg-[var(--surface-muted)] ring-1 ring-[var(--border)]">
          <div
            className="h-full rounded-full bg-brand-600 transition-[width] duration-500 ease-[var(--ease-out-soft)]"
            style={{ width: `${(erledigt / liste.length) * 100}%` }}
          />
        </div>

        {fehler && (
          <div className="mt-4">
            <Alert tone="error">{fehler}</Alert>
          </div>
        )}
        {p && !p.erreichbar && (
          <div className="mt-4">
            <Alert tone="error">{p.fehler ?? 'Die Seite konnte nicht geladen werden.'}</Alert>
          </div>
        )}
        {p?.erreichbar && (
          <p className="mt-4 text-xs text-[var(--text-muted)]">
            Geprüft am {new Date(p.geprueftAm!).toLocaleString('de-DE')}: {p.url}, {p.skriptZahl} Skripte im HTML.
            Geprüft wird das ausgelieferte HTML der Startseite. Was erst durch JavaScript entsteht, findet nur der
            Scanner.
          </p>
        )}
      </Card>

      <PromptKarte site={site} snippet={snippet} />

      {p?.erreichbar && p.seiten && p.seiten.length > 0 && <Seitenliste pruefung={p} />}

      {liste.map((schritt, index) => (
        <SchrittKarte
          key={schritt.id}
          nummer={index + 1}
          schritt={schritt}
          erledigtAm={schritte[schritt.id]}
          onToggle={() => void umschalten(schritt.id)}
        />
      ))}
    </div>
  );
}

/**
 * Alle gefundenen Seiten mit ihrem Einbaustand.
 *
 * Das Script muss auf jeder einzelnen Seite stehen. Liegt es nur auf der
 * Startseite, erscheint auf allen anderen kein Banner und es wird nichts
 * blockiert, ohne dass irgendwo ein Fehler auftaucht. Genau dieser Fall trat
 * beim ersten Einbau auf und war von aussen nicht zu sehen.
 */
function Seitenliste({ pruefung }: { pruefung: EmbedCheck }) {
  const seiten = pruefung.seiten ?? [];
  const mitLoader = seiten.filter((s) => s.loader).length;
  const fehlen = seiten.filter((s) => s.status === 200 && !s.loader);
  const unerreichbar = seiten.filter((s) => s.status !== 200);
  const alleGut = fehlen.length === 0 && seiten.length > 0;

  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-semibold">Seiten der Website</h3>
        <span
          className={cx(
            'rounded-full px-2.5 py-1 text-xs font-semibold',
            alleGut ? 'bg-ok-100 text-ok-500' : 'bg-warn-100 text-[#7c2d12]',
          )}
        >
          {mitLoader} von {seiten.length} mit Script
        </span>
      </div>

      <p className="mt-1.5 text-sm text-[var(--text-muted)]">
        Gefunden über die internen Links der Startseite. Das Script gehört in den <code>&lt;head&gt;</code> jeder Seite,
        sonst erscheint dort kein Banner und es wird nichts blockiert.
        {pruefung.seitenAbgeschnitten && ' Es wurden nicht alle Seiten geprüft, die Liste ist bei 25 begrenzt.'}
      </p>

      {fehlen.length > 0 && (
        <div className="mt-3">
          <Alert tone="warn">
            Auf {fehlen.length} {fehlen.length === 1 ? 'Seite fehlt' : 'Seiten fehlt'} das Script. Dort läuft Tracking
            ungehindert, und ein Besucher, der dort einsteigt, sieht kein Banner.
          </Alert>
        </div>
      )}

      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--border)] text-left text-xs uppercase tracking-wide text-[var(--text-muted)]">
              <th className="py-2 pr-3 font-semibold">Seite</th>
              <th className="py-2 px-2 text-center font-semibold">Script</th>
              <th className="py-2 px-2 text-center font-semibold">Footer</th>
              <th className="py-2 pl-2 text-center font-semibold">Erklärung</th>
            </tr>
          </thead>
          <tbody>
            {seiten.map((s) => (
              <tr key={s.url} className="border-b border-[var(--border)]/60 last:border-0">
                <td className="py-2 pr-3">
                  <span className="tabular block max-w-[22rem] truncate" title={s.url}>
                    {pfad(s.url)}
                  </span>
                  {s.status !== 200 && (
                    <span className="text-xs text-danger-500">
                      {s.status === 0 ? 'nicht erreichbar' : `HTTP ${s.status}`}
                    </span>
                  )}
                </td>
                <td className="px-2 text-center">
                  <Zeichen ok={s.loader} wichtig />
                </td>
                <td className="px-2 text-center">
                  <Zeichen ok={s.footerLink} />
                </td>
                <td className="pl-2 text-center">
                  <Zeichen ok={s.erklaerung} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-3 text-xs text-[var(--text-muted)]">
        Footer-Link und Cookie-Erklärung sind nicht auf jeder Seite nötig. Der Widerrufslink gehört überall hin, wo ein
        Footer ist, die Erklärung nur auf die Datenschutzseite.
        {unerreichbar.length > 0 && ` ${unerreichbar.length} Adresse(n) waren nicht abrufbar.`}
      </p>
    </Card>
  );
}

function Zeichen({ ok, wichtig }: { ok: boolean; wichtig?: boolean }) {
  return (
    <span
      className={cx(
        'inline-block font-semibold',
        ok ? 'text-ok-500' : wichtig ? 'text-danger-500' : 'text-[var(--text-muted)]',
      )}
      aria-label={ok ? 'vorhanden' : 'fehlt'}
    >
      {ok ? '✓' : wichtig ? '✗' : '–'}
    </span>
  );
}

function pfad(url: string): string {
  try {
    const u = new URL(url);
    return u.pathname === '/' ? '/ (Startseite)' : u.pathname;
  } catch {
    return url;
  }
}

function SchrittKarte({
  nummer,
  schritt,
  erledigtAm,
  onToggle,
}: {
  nummer: number;
  schritt: Schritt;
  erledigtAm?: string;
  onToggle: () => void;
}) {
  const [kopiert, setKopiert] = useState(false);

  async function kopieren() {
    if (!schritt.code) return;
    await navigator.clipboard.writeText(schritt.code).catch(() => undefined);
    setKopiert(true);
    window.setTimeout(() => setKopiert(false), 2500);
  }

  return (
    <Card className={cx(erledigtAm && 'opacity-70')}>
      <div className="flex gap-4">
        <span
          className={cx(
            'grid size-8 shrink-0 place-items-center rounded-full text-sm font-semibold',
            erledigtAm ? 'bg-ok-100 text-ok-500' : 'bg-brand-600 text-white',
          )}
          aria-hidden="true"
        >
          {erledigtAm ? '✓' : nummer}
        </span>

        <div className="min-w-0 flex-1">
          <h3 className={cx('font-semibold', erledigtAm && 'line-through')}>{schritt.titel}</h3>
          <div className="mt-1.5 text-sm leading-relaxed text-[var(--text-muted)]">{schritt.beschreibung}</div>

          {schritt.code && (
            <div className="mt-3">
              <pre className="tabular overflow-x-auto rounded-xl bg-ink-900 p-3.5 text-xs leading-relaxed text-brand-100">
                {schritt.code}
              </pre>
              <Button size="sm" variant="secondary" className="mt-2" onClick={kopieren}>
                {kopiert ? 'Kopiert' : 'Kopieren'}
              </Button>
            </div>
          )}

          {schritt.befund && (
            <p
              className={cx(
                'mt-3 flex items-start gap-2 rounded-lg px-3 py-2 text-xs',
                schritt.befund.ok ? 'bg-ok-100 text-ok-500' : 'bg-warn-100 text-[#7c2d12]',
              )}
            >
              <span aria-hidden="true">{schritt.befund.ok ? '✓' : '!'}</span>
              <span>{schritt.befund.text}</span>
            </p>
          )}

          <label className="mt-3 flex min-h-11 cursor-pointer items-center gap-2.5 text-sm font-medium">
            <input
              type="checkbox"
              checked={Boolean(erledigtAm)}
              onChange={onToggle}
              className="size-5 cursor-pointer accent-[var(--color-brand-600)]"
            />
            Erledigt
            {erledigtAm && (
              <span className="text-xs font-normal text-[var(--text-muted)]">
                am {new Date(erledigtAm).toLocaleDateString('de-DE')}
              </span>
            )}
          </label>
        </div>
      </div>
    </Card>
  );
}

function kurz(url: string): string {
  try {
    const u = new URL(url);
    return u.hostname + (u.pathname.length > 1 ? u.pathname.slice(0, 20) : '');
  } catch {
    return url.slice(0, 40);
  }
}

export { Spinner };
